import React, {useCallback, useEffect, useMemo, useState} from "react";
import { getPostAllComments } from "../services/comments";
import { RESTPost, RESTComment } from "../services/types";
import "./BDPost.css";
import CommentsPreview from "./CommentsPreview";
import CommentsDrawer from "./CommentsDrawer";
import VerbiWidget from "./VerbiWidget";
import Liker from "./Liker";
import ModerationButton from "./ModerationButton";
import ModerationDrawer from "./ModerationDrawer";
import AuthorButton from "./AuthorButton";
import AuthorDrawer from "./AuthorDrawer";
import Avatar from "./Avatar";
import {Link} from "react-router-dom";
import CommentForm from "./CommentForm";
import { formatFriendlyDate } from "../utils/dateFormat";
import { NewspaperIcon } from '@heroicons/react/24/solid';
import bdHidden from "../assets/bd_hidden.png";

const VERBI_CATEGORY_ID = 7615;

interface BDPostProps {
    post: RESTPost;
}

const BDPost: React.FC<BDPostProps> = ({ post }) => {
    const [comments, setComments] = useState<RESTComment[]>(post.comments || []);
    const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
    const [isModerationDrawerOpen, setIsModerationDrawerOpen] = useState(false);
    const [isAuthorDrawerOpen, setIsAuthorDrawerOpen] = useState(false);
    const [facebookVoteCount, setFacebookVoteCount] = useState(post.meta.facebook_votes?.length || 0);
    const [onFacebook, setOnFacebook] = useState(Boolean(post.meta.on_facebook));
    const [isTrashed, setIsTrashed] = useState(post.status === 'trash');
    const [orthoCorrection, setOrthoCorrection] = useState(post.meta.ortho_correction || '');
    const [isRevealed, setIsRevealed] = useState(false);

    const postLocalDate = useMemo(() => 
        post.date ? formatFriendlyDate(post.date) : '', 
        [post.date]
    );

    const isVerbiPost = useMemo(() => 
        post.categories.includes(VERBI_CATEGORY_ID) && post.verbi, 
        [post.categories, post.verbi]
    );

    const isHidden = post.meta.hidden;
    const showHiddenOverlay = isHidden && !isRevealed;

    const refreshComments = useCallback(async () => {
        const newComments = await getPostAllComments(post.id);
        setComments(newComments);
    }, [post.id]);

    const handleCommentCreated = useCallback(async () => {
        await refreshComments();
    }, [refreshComments]);

    const handleCommentDeleted = useCallback(async () => {
        await refreshComments();
    }, [refreshComments]);

    const handleToggleReveal = useCallback(() => {
        setIsRevealed(prev => !prev);
    }, []);

    const handleReveal = useCallback(() => {
        setIsRevealed(true);
    }, []);

    useEffect( () => {
        // Only fetch comments if they weren't provided with the post
        if (!post.comments) {
            refreshComments().catch(console.error);
        }
    }, [post.comments, refreshComments]);

    const handleOpenModerationDrawer = useCallback(() => {
        setIsModerationDrawerOpen(true);
    }, []);

    const handleOpenAuthorDrawer = useCallback(() => {
        setIsAuthorDrawerOpen(true);
    }, []);

    const handleCloseModerationDrawer = useCallback(() => {
        setIsModerationDrawerOpen(false);
    }, []);

    const handleCloseAuthorDrawer = useCallback(() => {
        setIsAuthorDrawerOpen(false);
    }, []);

    const handleCloseCommentsDrawer = useCallback(() => {
        setIsCommentsModalOpen(false);
    }, []);

    const handleOpenCommentsDrawer = useCallback(() => {
        setIsCommentsModalOpen(true);
    }, []);

    const handlePostDeleted = useCallback(() => {
        window.location.reload();
    }, []);

    const hasNewsArticle = post.meta.newsArticleURL?.trim();
    const hasContent = post.content.rendered.trim();

    return (
        <article className="BDPost" key={post.id}>
            <section className="header">
                <div className="header-actions">
                    <ModerationButton 
                        postId={post.id} 
                        onClick={handleOpenModerationDrawer}
                        facebookVoteCount={facebookVoteCount}
                        onFacebook={onFacebook}
                        isTrashed={isTrashed}
                        orthoCorrection={orthoCorrection}
                    />
                    <AuthorButton 
                        post={post}
                        onClick={handleOpenAuthorDrawer}
                        isTrashed={isTrashed}
                        orthoCorrection={orthoCorrection}
                    />
                    <Liker count={post.meta.ratings_score} postID={post.id}/>
                </div>
                <h1 className={isTrashed ? 'trashed' : ''} dangerouslySetInnerHTML={{__html: post.title.rendered}} />
                <p className="details">
                    Par
                    <Link to={`/author/${post.author}`} className="author">
                        <Avatar 
                            url={post.author_avatar} 
                            alt={post.author_name} 
                            size="medium"
                        />
                        <b>{post.author_name}</b>
                    </Link>
                    <time dateTime={post.date ?? undefined}>{postLocalDate}</time>
                    {hasNewsArticle && (
                        <>
                            &nbsp;•&nbsp;
                            <a 
                                href={post.meta.newsArticleURL} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="news-article-link"
                                title="Lire l'article référencé"
                            >
                                <NewspaperIcon className="news-article-icon" />
                                Lire l'article référencé
                            </a>
                        </>
                    )}
                </p>
            </section>
            <section className="bd">
                <div className="bd-image-container">
                    <img src={post.meta["large-image"]} alt={post.title.raw} loading="lazy"/>
                    {showHiddenOverlay && (
                        <button 
                            className="bd-hidden-badge" 
                            onClick={handleReveal}
                            aria-label="Révéler l'image"
                        >
                            <img src={bdHidden} alt="" loading="lazy"/>
                        </button>
                    )}
                    <div className="bd-badges">
                        {post.meta.certified && (
                            <div className="bd-certified-badge">BD Inédite</div>
                        )}
                        {isHidden && (
                            <button 
                                className="bd-hidden-toggle-badge"
                                onClick={handleToggleReveal}
                                aria-label={isRevealed ? "Cacher l'image" : "Révéler l'image"}
                            >
                                BD cachée
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {hasContent && (
                <section className="bd-description">
                    <div dangerouslySetInnerHTML={{__html: post.content.rendered}} />
                </section>
            )}

            {isVerbiPost && (
                <VerbiWidget postId={post.id} postVerbi={post.verbi!} />
            )}

            <section className="comments">
                {comments.length > 0 && (
                    <CommentsPreview 
                        comments={comments} 
                        onShowAllClick={handleOpenCommentsDrawer}
                        onCommentDeleted={handleCommentDeleted}
                    />
                )}
                <CommentForm postId={post.id} onCommentCreated={handleCommentCreated}/>
            </section>

            <CommentsDrawer
                isOpen={isCommentsModalOpen}
                onClose={handleCloseCommentsDrawer}
                comments={comments}
                postId={post.id}
                onCommentCreated={handleCommentCreated}
                onCommentDeleted={handleCommentDeleted}
            />

            <ModerationDrawer
                isOpen={isModerationDrawerOpen}
                onClose={handleCloseModerationDrawer}
                post={post}
                onVoteToggled={setFacebookVoteCount}
                onPromotionToggled={setOnFacebook}
                onTrashToggled={setIsTrashed}
                onOrthoCorrectionChanged={setOrthoCorrection}
            />

            <AuthorDrawer
                isOpen={isAuthorDrawerOpen}
                onClose={handleCloseAuthorDrawer}
                post={post}
                orthoCorrection={orthoCorrection}
                onPostDeleted={handlePostDeleted}
            />
        </article>
    );
}

export default BDPost;